import { useForm, Controller } from 'react-hook-form';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { ShouldRender } from 'components/ShouldRender';
import { MultiSelect } from 'components/form-elements/selects/MultiSelect';
import { useCreateGroup } from 'services/2n/groups';
import { useGetUsers } from 'services/2n/get-all-users';
import { useUserPermissions } from 'services/user/permissions';
import { yupResolver } from '@hookform/resolvers/yup';
import { useModal } from 'store/modals';
import styles from '../../../styles.module.scss';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import { useGroupFormSchema } from '../validation';
import { discardChanges } from '../../../../discardChanges';

export const AddGroupForm = () => {
  const intl = useIntl();
  const modal = useModal();
  const accessManagement2NModal = useAccessManagement2NModal();
  const { updateSelected, updateAdding } = useAccessManagement2NModal();
  const { users } = useGetUsers();
  const { checkPermission } = useUserPermissions();
  const usersInThisGroupOptions = users.map((user) => ({ value: user.Id, label: user.Name, email: user.Email }));
  const { mutateAsync: createGroup, isLoading: isCreating } = useCreateGroup();

  const isAdmin = checkPermission('admin-users', 'create');
  const isSystemAdmin = checkPermission('system-admin', 'create');
  const groupFormSchema = useGroupFormSchema();

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(groupFormSchema),
    defaultValues: {
      name: isAdmin && !isSystemAdmin ? 'GH-' : '',
      usersInThisGroup: [],
    },
  });

  const onSubmit = async (data) => {

    await createGroup({
      name: data.name,
      users: (data.usersInThisGroup || []).map((u) => ({ id: u.value })),
    }, {
      onSuccess: (group) => {
        updateSelected('group', group);
        updateAdding('group', false);
      },
    });

  };

  useEffect(() => {
    if (isDirty) {
      accessManagement2NModal.updateSavedChanges('group', isDirty);
    }
  }, [isDirty]);

  return (
    <div className={styles.container}>
      <Brikk.Heading
        heading="accessGroupsAddNew"
      />

      <Form
        id="add-group-form"
        className={styles.formContainer}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.formWrapper}>
          <Grid
            noPadding
            oneColumn
          >
            <div className={styles.form}>

              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="accessGroupsName"
                    id="group-name"
                    value={value}
                    inputRef={ref}
                    placeholder={isAdmin && !isSystemAdmin ? 'GH-XXXXXXX' : ''}
                    onChange={(event) => {
                      if (isAdmin && !isSystemAdmin) {
                        const inputValue = event.target.value;
                        if (inputValue === '' || inputValue.toUpperCase() === 'GH-') {
                          onChange('');
                          return;
                        }
                        const cleanValue = inputValue.replace(/^GH-/i, '');
                        onChange(`GH-${cleanValue}`);
                      } else {
                        onChange(event.target.value);
                      }
                    }}
                    hasError={!!errors.name}
                  />
                )}
              />

              <ShouldRender condition={!!errors.name}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.name?.message}
                      defaultMessage={errors.name?.message}
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="usersInThisGroup"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    options={usersInThisGroupOptions}
                    title="accessGroupsUsersInGroup"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

            </div>
          </Grid>
        </div>

        <div className={styles.formButtons}>
          <Button
            basic
            name="cancel-add-group"
            type="button"
            disabled={isCreating}
            onClick={() => discardChanges('group', null, intl, accessManagement2NModal, modal)}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            primary
            type="submit"
            name="submit-add-group"
            disabled={!isDirty || isCreating}
            loading={isCreating}
          >
            <FormattedMessage id="accessGroupsAddNew" />
          </Button>
        </div>
      </Form>

    </div>
  );
};
